import React, {FC} from 'react';
import messages from './messages';
import Breadcrumbs from 'components/Common/Breadcrumb';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {Card, CardBody, Col, Container, Row} from 'reactstrap';
import AppBarActions from 'components/VerticalLayout/app-bar-actions';
import Button from 'components/button';
import {Check, Plus} from 'react-feather';
import {useEditProductWeb} from './use-edit-product-web';
import Icon from '@ailibs/feather-react-ts';
import {LANGUAGES} from 'constants/language';
import {LocalizedTextInput} from 'components/text-input/multi';
import {LocalizedTextEditor} from 'components/text-editor/multi-lang';

interface Props {}

const EditProductWebPage: FC<Props> = () => {
  const {t} = useTranslation();

  const {
    params,
    validationErrors,
    isLoading,
    skill_fr,
    skill_en,
    reassurance_information_en,
    reassurance_information_fr,
    set_skill_en,
    set_reassurance_information_en,
    set_reassurance_information_fr,
    set_skill_fr,
    setParams,
    handleAddSize,
    handleChangeSize,
    handleRemoveSize,
    handleSubmit,
  } = useEditProductWeb();

  return (
    <div className="page-content">
      <Helmet>
        <title> {t(...messages.pageTitle())}</title>
      </Helmet>
      <Container fluid>
        <Breadcrumbs
          breadcrumbItems={[
            {title: t('common.dashboard'), path: '/dashboard'},
            {title: t(...messages.products()), path: '/dashboard/products'},
            {
              title: t(...messages.masterProductsWeb()),
              path: '/dashboard/master-products-web',
            },
            {title: t(...messages.pageTitle()), path: '#'},
          ]}
        />
        <Card>
          <CardBody>
            <Row className="mb-5">
              <LocalizedTextInput
                languages={LANGUAGES}
                label={lang =>
                  t(...messages.name(), {
                    lang: lang,
                  })
                }
                value={lang => params[`name_${lang}`]}
                onChange={(value, lang) => {
                  setParams({
                    ...params,
                    [`name_${lang}`]: value,
                  });
                }}
                errors={[
                  validationErrors[`name_en`],
                  validationErrors[`name_fr`],
                ].flat()}
              />
            </Row>
            <Row className="mb-5">
              <LocalizedTextEditor
                id={lang => `reassurance_information_${lang}`}
                languages={LANGUAGES}
                label={lang =>
                  t(...messages.reassuranceInformations(), {
                    lang: lang,
                  })
                }
                value={lang =>
                  lang === 'fr'
                    ? reassurance_information_fr
                    : reassurance_information_en
                }
                onChange={(value, lang) => {
                  if (lang === 'fr') {
                    set_reassurance_information_fr(value);
                  } else {
                    set_reassurance_information_en(value);
                  }
                }}
                errors={[
                  validationErrors[`reassurance_information_en`],
                  validationErrors[`reassurance_information_fr`],
                ].flat()}
              />
            </Row>
            <Row className="mb-5">
              <LocalizedTextEditor
                id={lang => `skill_${lang}`}
                languages={LANGUAGES}
                label={lang =>
                  t(...messages.knowledge(), {
                    lang: lang,
                  })
                }
                value={lang => (lang === 'fr' ? skill_fr : skill_en)}
                onChange={(value, lang) => {
                  if (lang === 'fr') {
                    set_skill_fr(value);
                  } else {
                    set_skill_en(value);
                  }
                }}
                errors={[
                  validationErrors[`skill_fr`],
                  validationErrors[`skill_en`],
                ].flat()}
              />
            </Row>
            <Button color="important" className="mb-5" onClick={handleAddSize}>
              <Plus size={18} /> {t('createProductWebPage.addSize')}
            </Button>
            {params.sizes.map(({id, value}, index) => (
              <Row
                key={id}
                className="mb-3 justify-content-center align-items-center"
              >
                <Col>
                  <LocalizedTextInput
                    languages={LANGUAGES}
                    label={lang =>
                      t(...messages.name(), {
                        lang: lang,
                      })
                    }
                    value={lang => value[lang]}
                    onChange={(value, lang) =>
                      handleChangeSize(id, value, lang)
                    }
                    errors={[
                      validationErrors[`sizes.${index}.fr`],
                      validationErrors[`sizes.${index}.en`],
                    ].flat()}
                  />
                </Col>
                <Col>
                  <Button
                    color="link"
                    className="d-flex align-items-center"
                    onClick={() => handleRemoveSize(id)}
                  >
                    <Icon name="trash-2" size={20} />
                  </Button>
                </Col>
              </Row>
            ))}

            <AppBarActions>
              <Button
                isLoading={isLoading}
                color="important"
                onClick={handleSubmit}
              >
                <Check size={15} /> {t('common.saveChanges')}
              </Button>
            </AppBarActions>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default EditProductWebPage;
